import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Divider,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Assessment as ReportsIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingIcon,
  AutoAwesome as AgentsIcon,
  Chat as ChatIcon,
  People as PeopleIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR, enUS, es } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import {
  LeadConversionReport,
  AgentPerformanceReport,
  ConversationAnalyticsReport,
  UserActivityReport,
  LeadConversionRequest,
  AgentPerformanceRequest,
  ConversationAnalyticsRequest,
  UserActivityRequest,
  FilterOptions
} from '../types/reports';
import { reportsApi } from '../utils/reportsApi';
import { ReportExporter } from '../utils/reportExport';
import LeadConversionChart from '../components/reports/LeadConversionChart';
import AgentPerformanceChart from '../components/reports/AgentPerformanceChart';
import ConversationAnalyticsChart from '../components/reports/ConversationAnalyticsChart';
import UserActivityChart from '../components/reports/UserActivityChart';
import ReportsFilters from '../components/reports/ReportsFilters';
import ReportsSummary from '../components/reports/ReportsSummary';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ 
          p: isMobile ? 1 : 3,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ReportsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);

  // Report data states
  const [leadConversionData, setLeadConversionData] = useState<LeadConversionReport | null>(null);
  const [agentPerformanceData, setAgentPerformanceData] = useState<AgentPerformanceReport | null>(null);
  const [conversationAnalyticsData, setConversationAnalyticsData] = useState<ConversationAnalyticsReport | null>(null);
  const [userActivityData, setUserActivityData] = useState<UserActivityReport | null>(null);

  // Filter states
  const [filters, setFilters] = useState({
    startDate: null as Date | null,
    endDate: null as Date | null,
    agentId: '',
    userId: '',
    leadQuality: '',
    conversationStatus: '',
    agentStatus: '',
    status: '',
    typeProduct: '',
    groupBy: 'day'
  });

  useEffect(() => {
    loadFilterOptions();
    loadInitialData();
  }, []);

  const loadFilterOptions = async () => {
    try {
      const options = await reportsApi.getFilterOptions();
      setFilterOptions(options);
    } catch (err) {
      console.error('Erro ao carregar opções de filtro:', err);
      // Error is logged but not shown to user to avoid noise
    }
  };

  const loadInitialData = async () => {
    await Promise.all([
      loadLeadConversionData(),
      loadAgentPerformanceData(),
      loadConversationAnalyticsData(),
      loadUserActivityData()
    ]);
  };

  const loadLeadConversionData = async () => {
    try {
      setLoading(true);
      setError(null);
      const request: LeadConversionRequest = {
        startDate: filters.startDate?.toISOString().split('T')[0],
        endDate: filters.endDate?.toISOString().split('T')[0],
        agentId: filters.agentId || undefined,
        userId: filters.userId || undefined,
        leadQuality: filters.leadQuality as any || undefined,
        status: filters.status || undefined,
        groupBy: filters.groupBy as any || 'day',
        includeFollowUpStats: true,
        includeQualityBreakdown: true
      };
      const data = await reportsApi.getLeadConversionReport(request);
      setLeadConversionData(data);
    } catch (err) {
      setError(t('pages.reports.page.errors.loadLeadConversion'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAgentPerformanceData = async () => {
    try {
      const request: AgentPerformanceRequest = {
        startDate: filters.startDate?.toISOString().split('T')[0],
        endDate: filters.endDate?.toISOString().split('T')[0],
        agentId: filters.agentId || undefined,
        agentStatus: filters.agentStatus as any || undefined,
        typeProduct: filters.typeProduct as any || undefined,
        groupBy: filters.groupBy as any || 'day',
        includeResponseTime: true,
        includeChatStats: true
      };
      const data = await reportsApi.getAgentPerformanceReport(request);
      setAgentPerformanceData(data);
    } catch (err) {
      console.error('Erro ao carregar relatório de performance dos agentes:', err);
      // Error is logged but not shown to user to avoid noise
    }
  };

  const loadConversationAnalyticsData = async () => {
    try {
      const request: ConversationAnalyticsRequest = {
        startDate: filters.startDate?.toISOString().split('T')[0],
        endDate: filters.endDate?.toISOString().split('T')[0],
        agentId: filters.agentId || undefined,
        userId: filters.userId || undefined,
        conversationStatus: filters.conversationStatus as any || undefined,
        groupBy: filters.groupBy as any || 'day',
        includeMessageCount: true,
        includeResponseTime: true,
        includeStatusBreakdown: true
      };
      const data = await reportsApi.getConversationAnalyticsReport(request);
      setConversationAnalyticsData(data);
    } catch (err) {
      console.error('Erro ao carregar relatório de analytics de conversas:', err);
      // Error is logged but not shown to user to avoid noise
    }
  };

  const loadUserActivityData = async () => {
    try {
      const request: UserActivityRequest = {
        startDate: filters.startDate?.toISOString().split('T')[0],
        endDate: filters.endDate?.toISOString().split('T')[0],
        userId: filters.userId || undefined,
        groupBy: filters.groupBy as any || 'day',
        includeLeadAssignment: true,
        includeConversationHandling: true
      };
      const data = await reportsApi.getUserActivityReport(request);
      setUserActivityData(data);
    } catch (err) {
      console.error('Erro ao carregar relatório de atividade dos usuários:', err);
      // Error is logged but not shown to user to avoid noise
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    loadInitialData();
    setShowFilters(false);
  };

  const handleRefresh = () => {
    loadInitialData();
  };

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExport = (format: 'excel' | 'csv') => {
    const reportTypes = ['LeadConversion', 'AgentPerformance', 'ConversationAnalytics', 'UserActivity'];
    const currentReportType = reportTypes[activeTab];

    let currentData;
    switch (activeTab) {
      case 0:
        currentData = leadConversionData;
        break;
      case 1:
        currentData = agentPerformanceData;
        break;
      case 2:
        currentData = conversationAnalyticsData;
        break;
      case 3:
        currentData = userActivityData;
        break;
      default:
        return;
    }

    if (!currentData) {
      alert(t('pages.reports.page.export.noData'));
      return;
    }

    try {
      if (format === 'excel') {
        ReportExporter.exportToExcel(currentData, currentReportType);
      } else {
        ReportExporter.exportToCSV(currentData, currentReportType);
      }
      handleExportMenuClose();
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
      alert(t('pages.reports.page.export.error'));
    }
  };

  const getTabIcon = (index: number) => {
    const icons = [TrendingIcon, AgentsIcon, ChatIcon, PeopleIcon];
    const Icon = icons[index];
    return <Icon />;
  };

  const getTabLabel = (index: number) => {
    const labels = [
      t('pages.reports.page.tabs.leadConversion'),
      t('pages.reports.page.tabs.agentPerformance'),
      t('pages.reports.page.tabs.conversationAnalytics'),
      t('pages.reports.page.tabs.userActivity')
    ];
    return labels[index];
  };

  const getDateLocale = () => {
    const lang = i18n.language;
    if (lang === 'pt-BR') return ptBR;
    if (lang === 'es-ES') return es;
    return enUS;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateLocale()}>
      <Box sx={{ 
        width: '100%', 
        maxWidth: '100vw', 
        overflow: 'hidden',
        px: isMobile ? 1 : 0
      }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ReportsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.page.title')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={t('pages.reports.page.filters')}>
                <IconButton
                  onClick={() => setShowFilters(!showFilters)}
                  color={showFilters ? 'primary' : 'default'}
                >
                  <FilterIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('pages.reports.page.refresh')}>
                <IconButton onClick={handleRefresh} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('pages.reports.page.export')}>
                <IconButton onClick={handleExportClick}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Filters Panel */}
          {showFilters && (
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.page.filters')}
              </Typography>
              <ReportsFilters
                filters={filters}
                filterOptions={filterOptions}
                onFilterChange={handleFilterChange}
                onApply={handleApplyFilters}
                onCancel={() => setShowFilters(false)}
              />
            </Paper>
          )}

          {/* Summary Cards */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        {t('pages.reports.page.summary.totalLeads')}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {leadConversionData?.totalLeads || '-'}
                      </Typography>
                    </Box>
                    <TrendingIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        {t('pages.reports.page.summary.conversionRate')}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {leadConversionData?.conversionRate.toFixed(1) || '-'}%
                      </Typography>
                    </Box>
                    <TrendingIcon sx={{ fontSize: 40, color: 'success.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        {t('pages.reports.page.summary.activeAgents')}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {agentPerformanceData?.activeAgents || '-'}
                      </Typography>
                    </Box>
                    <AgentsIcon sx={{ fontSize: 40, color: 'warning.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        {t('pages.reports.page.summary.activeConversations')}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {conversationAnalyticsData?.activeConversations || '-'}
                      </Typography>
                    </Box>
                    <ChatIcon sx={{ fontSize: 40, color: 'info.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading Indicator */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Tabs */}
        <Paper elevation={2} sx={{
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 64,
                textTransform: 'none',
                fontWeight: 'bold'
              }
            }}
          >
            {[0, 1, 2, 3].map((index) => (
              <Tab
                key={index}
                icon={getTabIcon(index)}
                label={getTabLabel(index)}
                iconPosition="start"
                sx={{ minHeight: 64 }}
              />
            ))}
          </Tabs>

          {/* Tab Panels */}
          <TabPanel value={activeTab} index={0}>
            {leadConversionData && (
              <LeadConversionChart data={leadConversionData} />
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            {agentPerformanceData && (
              <AgentPerformanceChart data={agentPerformanceData} />
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            {conversationAnalyticsData && (
              <ConversationAnalyticsChart data={conversationAnalyticsData} />
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            {userActivityData && (
              <UserActivityChart data={userActivityData} />
            )}
          </TabPanel>
        </Paper>

        {/* Export Menu */}
        <Menu
          anchorEl={exportMenuAnchor}
          open={Boolean(exportMenuAnchor)}
          onClose={handleExportMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => handleExport('excel')}>
            <ListItemIcon>
              <ExcelIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('pages.reports.page.export.excel')}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleExport('csv')}>
            <ListItemIcon>
              <CsvIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('pages.reports.page.export.csv')}</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </LocalizationProvider>
  );
};

export default ReportsPage;
